import { DownloadModal } from './DownloadModal';
import { MapPreviewModal } from './MapPreviewModal';
import { Alert, Box, Heading, Icon, Select, Spinner, Table, Text } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled, IconInfoFilled } from '@zillow/constellation-icons';
import { Fragment, useMemo, useState } from 'react';
import { useAsync, useAsyncRetry } from 'react-use';
import { useQuery } from '@zg-rentals/react-query5';
import type {
  CustomerAnalyticsMoodysCreditDto,
  CustomerAnalyticsMoodysCreditOverviewDto,
  CustomerAnalyticsMoodysMarketDto,
} from '@zg-rentals/ts-customer-analytics-client';
import {
  CustomerAnalyticsMoodysWebApiHandler_canUserDownloadPremiumReport,
  CustomerAnalyticsMoodysWebApiHandler_getMoodysCreditOverview,
  CustomerAnalyticsMoodysWebApiHandler_getMoodysMarkets,
  CustomerAnalyticsMoodysWebApiHandler_getMoodysReport,
  CustomerAnalyticsMoodysWebApiHandler_getMoodysReport_Path,
} from '@zg-rentals/ts-customer-analytics-client';

import { ALL_US_STATES_AND_TERRITORIES_MAP, type US_STATES_LABEL_MAP_KEYS } from '@zg-rentals/states-territories';
import { httpClient } from '../../../httpClient';
import { useMainLayout } from '../../layouts/MainPageLayout/MainLayoutContext';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitIndustryReportsChooseStateEvent } from '../../../analytics/events';
import { CustomerAnalyticsPage } from '../../SharedComponents/CustomerAnalyticsPage';
import { IndustryReportsSidebar } from './IndustryReportsSidebar';

const DROPDOWN_OPTIONS = (() => {
  const statesTerritoriesCopy: Record<string, string> = { ...ALL_US_STATES_AND_TERRITORIES_MAP };
  delete statesTerritoriesCopy.AA;
  delete statesTerritoriesCopy.AK;
  delete statesTerritoriesCopy.AE;
  delete statesTerritoriesCopy.AP;
  delete statesTerritoriesCopy.FM;
  delete statesTerritoriesCopy.MH;
  delete statesTerritoriesCopy.PW;
  delete statesTerritoriesCopy.UM;

  return Object.entries(statesTerritoriesCopy).map(([key, value]) => ({
    value: key,
    label: value,
  }));
})();

type MarketTableType = {
  [metroId: string]: {
    market: CustomerAnalyticsMoodysMarketDto;
    submarkets: Array<CustomerAnalyticsMoodysMarketDto>;
  };
};

const MOODYS_AREA_TYPE_MARKET = 'MARKET';

export const IndustryReports = () => {
  const { user } = useRentalManagerUser();
  const [selectedState, setSelectedState] = useState<US_STATES_LABEL_MAP_KEYS>();
  const [expandedMarket, setExpandedMarket] = useState<string | undefined>();
  const downloadErrorState = useState(false);
  const { allProperties, selectedFilters, filteredProperties } = useMainLayout();

  const {
    value: markets,
    error: marketsError,
    loading: marketsIsLoading,
  } = useAsync(async () => CustomerAnalyticsMoodysWebApiHandler_getMoodysMarkets(httpClient), []);

  const {
    value: creditOverview,
    error: creditOverviewError,
    loading: creditOverviewIsLoading,
  } = useAsyncRetry<CustomerAnalyticsMoodysCreditOverviewDto>(
    async () => CustomerAnalyticsMoodysWebApiHandler_getMoodysCreditOverview(httpClient),
    [],
  );

  const { value: canDownloadPremium = false } = useAsync(
    async () => CustomerAnalyticsMoodysWebApiHandler_canUserDownloadPremiumReport(httpClient),
    [],
  );

  const isAllSingleFamily = allProperties?.every((property) => property.aggregateStats.isSingleFamily) ?? false;

  const marketTable: MarketTableType = useMemo(
    () =>
      markets?.reduce((acc: MarketTableType, market: CustomerAnalyticsMoodysMarketDto) => {
        if (market.areaType === MOODYS_AREA_TYPE_MARKET) {
          acc[market.metroId] = {
            market,
            submarkets:
              markets?.filter(
                (subMarket: CustomerAnalyticsMoodysMarketDto) =>
                  subMarket.submarketNr > 0 && subMarket.metroId === market.metroId,
              ) ?? [],
          };
        }
        return acc;
      }, {} as MarketTableType) ?? {},
    [markets],
  );

  const selectedSingleProperty =
    selectedFilters.listingAlias && filteredProperties.length === 1 ? filteredProperties[0] : undefined;

  const selectedListingAlias = selectedSingleProperty?.propertyDetails.listingAlias;
  const isSingleFamily = selectedSingleProperty?.aggregateStats.isSingleFamily ?? false;

  const { data: propertyMoodysReport, isLoading: isLoadingPropertyReport } = useQuery({
    queryKey: [CustomerAnalyticsMoodysWebApiHandler_getMoodysReport_Path, selectedListingAlias],
    queryFn: () =>
      CustomerAnalyticsMoodysWebApiHandler_getMoodysReport({ listingAlias: selectedListingAlias! }, httpClient),
    enabled: !!selectedListingAlias && !isSingleFamily,
  });

  const propertyMarket = useMemo(() => {
    if (!propertyMoodysReport || !markets) return undefined;
    const parentMarket = markets.find(
      (m) => m.areaNm === propertyMoodysReport.marketName && m.areaType === MOODYS_AREA_TYPE_MARKET,
    );
    if (!parentMarket) return undefined;
    if (!propertyMoodysReport.submarketName) return parentMarket;
    return markets.find((m) => m.areaNm === propertyMoodysReport.submarketName && m.metroId === parentMarket.metroId);
  }, [propertyMoodysReport, markets]);

  if (isAllSingleFamily) {
    return (
      <Box css={{ padding: '800', textAlign: 'center' }}>
        <Text>Moody&apos;s Industry Reports are available for multifamily properties only.</Text>
      </Box>
    );
  }

  const userIsSignature = canDownloadPremium;

  const showPreviousDownloads = !!(creditOverview && creditOverview.previousDownloads.length > 0);

  const stateOptionsWithMarkets = DROPDOWN_OPTIONS.filter((state) => {
    const stateMarkets = Object.values(marketTable).filter(
      (marketArea: { market: CustomerAnalyticsMoodysMarketDto; submarkets: Array<CustomerAnalyticsMoodysMarketDto> }) =>
        marketArea.market.state === state.value,
    );
    return stateMarkets && stateMarkets.length > 0;
  });

  const getMarketAndSubmarketFromDownload = (credit: CustomerAnalyticsMoodysCreditDto) => {
    const marketOrSubmarket = markets?.find(
      (market: CustomerAnalyticsMoodysMarketDto) => market.areaNm === credit.areaName,
    );

    return {
      metroId: marketOrSubmarket?.metroId || '',
      submarketId: marketOrSubmarket?.submarketNr,
      year: credit.year,
      quarter: credit.quarter,
    };
  };

  const downloadModalPopup = ({
    metroId,
    submarketId,
    year,
    quarter,
  }: {
    metroId: string;
    submarketId: number | undefined;
    year?: number;
    quarter?: number;
  }) => {
    return (
      <DownloadModal
        year={year || creditOverview?.latestYear || 0}
        quarter={quarter || creditOverview?.latestQuarter || 0}
        metroId={metroId}
        submarketId={submarketId}
        errorState={downloadErrorState}
        isSignatureUser={userIsSignature}
      />
    );
  };

  const mapModalPopup = (
    market: CustomerAnalyticsMoodysMarketDto,
    submarket: CustomerAnalyticsMoodysMarketDto | undefined,
  ) => (
    <MapPreviewModal
      areaName={submarket?.areaNm || market.areaNm}
      metroId={market.metroId}
      submarketId={submarket?.submarketNr}
    />
  );

  const getMarketRows = () => {
    const getIcon = (market: CustomerAnalyticsMoodysMarketDto, submarketRows: Array<JSX.Element>) => {
      if (market.areaId === expandedMarket && submarketRows.length > 0) {
        return (
          <Icon css={{ color: 'text.link' }}>
            <IconChevronUpFilled />
          </Icon>
        );
      } else if (market.areaId !== expandedMarket && submarketRows.length > 0) {
        return (
          <Icon css={{ color: 'text.link' }}>
            <IconChevronDownFilled />
          </Icon>
        );
      }
      return null;
    };

    return Object.values(marketTable).map((marketArea) => {
      const marketIsActive =
        marketArea.market.areaType === MOODYS_AREA_TYPE_MARKET && marketArea.market.state === selectedState;
      const submarketRows = marketIsActive ? getSubmarketRows(marketArea.market) : [];
      const hasSubmarkets = submarketRows.length > 0;

      return (
        marketIsActive && (
          <Fragment key={marketArea.market.areaId}>
            <Table.Row>
              <Table.Cell>
                {hasSubmarkets ? (
                  <button
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: '0',
                      font: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={() => {
                      setExpandedMarket((currentMarket) => {
                        if (currentMarket === marketArea.market.areaId) {
                          return undefined;
                        }
                        return marketArea.market.areaId;
                      });
                    }}
                  >
                    {getIcon(marketArea.market, submarketRows)}
                    <Text css={{ fontWeight: '600' }}>{marketArea.market.areaNm}</Text>
                    <Text css={{ fontSize: 'xs', color: 'text.secondary' }}>({submarketRows.length} submarkets)</Text>
                  </button>
                ) : (
                  <Text css={{ fontWeight: '600' }}>{marketArea.market.areaNm}</Text>
                )}
              </Table.Cell>
              <Table.Cell>
                <Text css={{ fontWeight: '700' }}>Market</Text>
              </Table.Cell>
              <Table.Cell>{mapModalPopup(marketArea.market, undefined)}</Table.Cell>
              <Table.Cell>
                {downloadModalPopup({ metroId: marketArea.market.metroId, submarketId: undefined })}
              </Table.Cell>
            </Table.Row>
            {expandedMarket === marketArea.market.areaId && submarketRows}
          </Fragment>
        )
      );
    });
  };

  const getSubmarketRows = (market: CustomerAnalyticsMoodysMarketDto) => {
    if (!marketTable[market.metroId]) return [] as Array<JSX.Element>;

    return marketTable[market.metroId]?.submarkets?.map((subMarket) => (
      <Table.Row key={`${subMarket.areaId}-${market.areaId}`}>
        <Table.Cell>
          <Box css={{ paddingLeft: '800' }}>
            <Text>{subMarket.areaNm}</Text>
          </Box>
        </Table.Cell>
        <Table.Cell>
          <Text css={{ fontWeight: '700' }}>Submarket</Text>
        </Table.Cell>
        <Table.Cell>{mapModalPopup(market, subMarket)}</Table.Cell>
        <Table.Cell>
          {downloadModalPopup({
            metroId: market.metroId,
            submarketId: subMarket.submarketNr,
          })}
        </Table.Cell>
      </Table.Row>
    ));
  };

  const renderPreviousDownloadsSection = () => (
    <Box css={{ marginTop: '800' }}>
      <Heading level={4} css={{ marginBottom: '600', fontWeight: '700' }}>
        Previous Downloads
      </Heading>
      <Box
        css={{
          backgroundColor: 'background.primary',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'border.subtle',
          overflow: 'hidden',
        }}
      >
        <Table.Responsive>
          <Table.Root
            aria-label="Previous Downloads"
            css={{
              '& td, & th': {
                padding: '600',
                verticalAlign: 'middle',
              },
              '& thead tr': {
                borderBottom: '1px solid',
                borderColor: 'border.subtle',
              },
            }}
            appearance="horizontal"
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Market/Submarket</Table.HeaderCell>
                <Table.HeaderCell>Credit From</Table.HeaderCell>
                <Table.HeaderCell>Data as of</Table.HeaderCell>
                <Table.HeaderCell>Download date</Table.HeaderCell>
                <Table.HeaderCell>Downloaded by</Table.HeaderCell>
                <Table.HeaderCell>Downloaded report</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {creditOverview?.previousDownloads.map((credit: CustomerAnalyticsMoodysCreditDto, index: number) => (
                <Table.Row key={`${credit.areaName}-${credit.reportQuarter}-${credit.year}-${index}`}>
                  <Table.Cell>
                    <Text css={{ fontWeight: '600' }}>{credit.areaName}</Text>
                  </Table.Cell>
                  <Table.Cell
                    css={{
                      overflowX: 'hidden',
                      textOverflow: 'ellipsis',
                      '&:hover': { overflowX: 'auto' },
                    }}
                  >
                    <Text>{credit.creditName}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text>{credit.reportQuarter}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text>{new Date(credit.creditSpentAt).toLocaleDateString()}</Text>
                  </Table.Cell>
                  <Table.Cell
                    css={{
                      overflowX: 'hidden',
                      textOverflow: 'ellipsis',
                      '&:hover': { overflowX: 'auto' },
                    }}
                  >
                    <Text>{credit.creditSpentBy}</Text>
                  </Table.Cell>
                  <Table.Cell>{downloadModalPopup(getMarketAndSubmarketFromDownload(credit))}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Table.Responsive>
      </Box>
    </Box>
  );

  const renderStateSelector = () => {
    return (
      <Box
        css={{
          marginTop: 'sm',
          backgroundColor: 'background.primary',
          borderRadius: '10px',
        }}
      >
        <Heading level={6} css={{ marginBottom: '600', fontWeight: '600' }}>
          Select a state
        </Heading>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '600', flexWrap: 'wrap' }}>
          <Select
            value={selectedState || '_'}
            onChange={(e) => {
              if (user) emitIndustryReportsChooseStateEvent({ user });
              setSelectedState(e.target.value as US_STATES_LABEL_MAP_KEYS);
            }}
            css={{ width: '500px' }}
          >
            <option value="_" disabled hidden>
              Ex: Alabama
            </option>
            {stateOptionsWithMarkets.map((state) => (
              <option value={state.value} key={state.value}>
                {state.label}
              </option>
            ))}
          </Select>
          {selectedState && (
            <Box css={{ display: 'flex', alignItems: 'center', gap: '300' }}>
              <Icon css={{ color: 'text.secondary' }}>
                <IconInfoFilled />
              </Icon>
              <Text css={{ color: 'text.secondary' }}>
                Showing all markets in the selected state with available data.
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderMarketsTable = () => {
    if (!selectedState) return null;

    return (
      <Box
        css={{
          marginTop: 'sm',
          backgroundColor: 'background.primary',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'border.subtle',
          overflow: 'hidden',
        }}
      >
        <Table.Responsive>
          <Table.Root
            aria-label="Available Markets and Submarkets"
            css={{
              tableLayout: 'fixed',
              width: '100%',
              '& td, & th': {
                padding: '600',
                verticalAlign: 'middle',
              },
              '& td:nth-child(1)': { width: '40%' },
              '& td:nth-child(2)': { width: '15%' },
              '& td:nth-child(3)': { width: '25%' },
              '& td:nth-child(4)': { width: '20%' },
            }}
            appearance="horizontal"
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Market/Submarket</Table.HeaderCell>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Map</Table.HeaderCell>
                <Table.HeaderCell>Download Report</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>{getMarketRows()}</Table.Body>
          </Table.Root>
        </Table.Responsive>
      </Box>
    );
  };

  const renderPropertyMarketSection = () => {
    if (isLoadingPropertyReport) {
      return (
        <Box css={{ marginTop: 'sm' }}>
          <Spinner size="sm" />
        </Box>
      );
    }

    if (!propertyMarket || !propertyMoodysReport) {
      return (
        <Box css={{ marginTop: 'sm' }}>
          <Alert tone="info">
            No Moody&apos;s market report is available for the selected property. Please select a state to browse
            available reports.
          </Alert>
        </Box>
      );
    }

    const parentMarket = Object.values(marketTable).find((m) => m.market.metroId === propertyMarket.metroId);

    return (
      <Box css={{ marginTop: 'sm' }}>
        <Heading level={6} css={{ marginBottom: '600', fontWeight: '600' }}>
          {selectedSingleProperty?.propertyDetails.listingName}
        </Heading>
        <Box
          css={{
            backgroundColor: 'background.primary',
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'border.subtle',
            overflow: 'hidden',
          }}
        >
          <Table.Responsive>
            <Table.Root
              aria-label="Selected property market"
              css={{
                '& td': {
                  paddingBlock: '400',
                  paddingInline: '600',
                  verticalAlign: 'middle',
                },
              }}
              appearance="horizontal"
            >
              <Table.Body>
                <Table.Row>
                  <Table.Cell>
                    <Text css={{ fontWeight: '600' }}>{propertyMarket.areaNm}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text css={{ fontWeight: '700' }}>{propertyMarket.submarketNr > 0 ? 'Submarket' : 'Market'}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    {parentMarket &&
                      mapModalPopup(parentMarket.market, propertyMarket.submarketNr > 0 ? propertyMarket : undefined)}
                  </Table.Cell>
                  <Table.Cell>
                    {downloadModalPopup({
                      metroId: propertyMarket.metroId,
                      submarketId: propertyMarket.submarketNr > 0 ? propertyMarket.submarketNr : undefined,
                    })}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </Table.Responsive>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (creditOverviewIsLoading || marketsIsLoading) {
      return (
        <Box css={{ display: 'flex', justifyContent: 'center', padding: '800' }}>
          <Spinner size="lg" />
        </Box>
      );
    }

    if (marketsError) {
      return (
        <Box css={{ padding: '600', textAlign: 'center' }}>
          <Text css={{ color: 'text.error', marginBottom: '400' }}>
            {marketsError.message || 'Failed to fetch market data, please try again later.'}
          </Text>
        </Box>
      );
    }

    if (creditOverviewError) {
      return (
        <Box css={{ padding: '600', textAlign: 'center' }}>
          <Text css={{ color: 'text.error', marginBottom: '400' }}>
            {creditOverviewError.message || 'Failed to credit data, please try again later.'}
          </Text>
        </Box>
      );
    }

    return (
      <>
        <Box
          css={{
            backgroundColor: 'backgroundWhite',
            padding: 'sm',
          }}
        >
          <Text css={{ fontSize: 'lg', lineHeight: '1.4', display: 'inline', float: 'right' }}>
            <Text css={{ fontWeight: '700' }}>Moody&apos;s Advanced Market Report</Text>
          </Text>
          {selectedSingleProperty && !isSingleFamily && renderPropertyMarketSection()}
          {renderStateSelector()}
          {renderMarketsTable()}
        </Box>
        {showPreviousDownloads && <>{renderPreviousDownloadsSection()}</>}
      </>
    );
  };

  return (
    <CustomerAnalyticsPage
      header="Industry reports"
      subheader="Source: Zillow internal data"
      sidebar={<IndustryReportsSidebar />}
      hideMarketSelect
    >
      {renderContent()}
    </CustomerAnalyticsPage>
  );
};
